import { Injectable, InjectionToken, Injector, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as _ from 'lodash-es';
import { Translation, TranslocoLoader, TranslocoService } from '@jsverse/transloco';
import { Config } from '../config/config.service';
import { ReadyService } from './ready.service';

export const MOBILE_TRANSLATION_CONFIG = new InjectionToken<{ localPath: string }>(
  'mobile-translation-config'
);

@Injectable()
export class TranslationLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  private config = inject(Config);
  private readyService = inject(ReadyService);
  private mobileConfig = inject(MOBILE_TRANSLATION_CONFIG, { optional: true });
  // Injector used to break circular dependency: TranslocoService → TRANSLOCO_LOADER → TranslationLoader
  private injector = inject(Injector);

  getTranslation(lang: string): Observable<Translation> {
    if (this.config.CONFIG.isMobile && this.mobileConfig) {
      return this.loadFromLocal(lang);
    }

    return this.loadFromCdn(lang);
  }

  private fetchAndMergeScopes(lang: string, baseUrl: string = this.config.CONFIG.localesUrl): Observable<Translation> {
    const scopes: string[] = this.config.CONFIG.scopes;
    const requests = scopes.map(scope => this.fetchScope(scope, lang, baseUrl));
    return forkJoin(requests).pipe(
      map(results => results.reduce((acc, partial) => _.merge(acc, partial), {} as Translation))
    );
  }

  private loadFromCdn(lang: string): Observable<Translation> {
    return this.fetchAndMergeScopes(lang).pipe(
      tap(() => this.readyService.setReady(true))
    );
  }

  private loadFromLocal(lang: string): Observable<Translation> {
    return this.fetchAndMergeScopes(lang, this.mobileConfig!.localPath).pipe(
      tap(() => {
        this.readyService.setReady(true);
        // TranslocoService resolved lazily via Injector to avoid circular DI at construction.
        // fetchCdnForMobileUpdate only emits on full success — CORS/4xx/network errors silently
        // complete via EMPTY, preserving the already-loaded local translations.
        this.fetchCdnForMobileUpdate(lang).subscribe(cdnTranslations => {
          this.injector.get(TranslocoService).setTranslation(cdnTranslations, lang);
        });
      })
    );
  }

  private fetchCdnForMobileUpdate(lang: string): Observable<Translation> {
    const baseUrl = this.config.CONFIG.localesUrl;
    const scopes: string[] = this.config.CONFIG.scopes;
    const requests = scopes.map(scope => this.fetchScopeStrict(scope, lang, baseUrl));
    return forkJoin(requests).pipe(
      map(results => results.reduce((acc, partial) => _.merge(acc, partial), {} as Translation)),
      catchError(() => EMPTY)
    );
  }

  private fetchScope(scope: string, lang: string, baseUrl: string): Observable<Translation> {
    const url = `${baseUrl}/${scope}/${lang}.json`;
    return this.http.get<Translation>(url).pipe(catchError(() => of({} as Translation)));
  }

  private fetchScopeStrict(scope: string, lang: string, baseUrl: string): Observable<Translation> {
    const url = `${baseUrl}/${scope}/${lang}.json`;
    return this.http.get<Translation>(url);
  }
}
